// Cross-surface constants — single source so the CLI, the API and the MCP can
// never drift on what counts as a valid relation or a healthy anchor.

// The typed graph edges. Consumed by the API's relations endpoint validation,
// the MCP rels schema, and (by value) the dashboard's relation picker.
export const RELATION_TYPES = ['relates_to', 'depends_on', 'supersedes', 'blocks', 'implements', 'documents', 'risk_for'] as const
export type RelationType = (typeof RELATION_TYPES)[number]

// Anchor health: a glob matching more files than this is almost always a
// catch-all that will rot (healthy topics anchor ~5-40 files). Shared by the
// CLI's local validator and the API's server-side anchor validation so both
// surfaces emit the same verdict for the same glob.
export const OVERBROAD_PATTERN_THRESHOLD = 100

// Paths that never count as real anchor matches (vendored/build output). Same
// filter on every surface.
export const NOISE_PATH = /(^|\/)(node_modules|dist|build|out|\.git|coverage|\.next|\.turbo|\.pnpm-store)(\/|$)/

// Work surface (projects + cards) vocabularies — one source for the API DTOs,
// the CLI, and the MCP JSON schema, which each hand-rolled these lists (4 copies
// of CARD_STATUSES, 3 of PROJECT_STATUSES). Add/rename a status HERE only.
export const CARD_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done'] as const
export type CardStatus = (typeof CARD_STATUSES)[number]

export const PROJECT_STATUSES = ['active', 'done', 'archived'] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

// What a project/card context ref can point at.
export const CONTEXT_REF_TYPES = ['topic', 'note', 'directory'] as const
export type ContextRefType = (typeof CONTEXT_REF_TYPES)[number]

// Max bytes for a topic/card markdown content body (hand-coded as 256*1024 in
// the API write path twice and the CLI once).
export const MAX_CONTENT_BYTES = 256 * 1024

// ── Active Work (WorkIntent) — the ephemeral execution layer over Cards ──────
// A WorkIntent is ONE running execution of a Card (a terminal session working
// it), never a durable primitive: the Card stays the unit of work. One source
// for the API DTOs/CHECKs, the CLI and the MCP schema.
export const WORK_INTENT_SOURCES = ['cli', 'mcp', 'hook', 'web'] as const
export type WorkIntentSource = (typeof WORK_INTENT_SOURCES)[number]

export const WORK_INTENT_LIFECYCLES = ['active', 'completed', 'abandoned', 'expired'] as const
export type WorkIntentLifecycle = (typeof WORK_INTENT_LIFECYCLES)[number]

// `conflict` exists only as a preflight VERDICT (no intent is created), so the
// stored preflight_verdict column carries clear|awareness only.
export const WORK_INTENT_VERDICTS = ['clear', 'awareness'] as const
export type WorkIntentVerdict = (typeof WORK_INTENT_VERDICTS)[number]

export const WORK_INTENT_OUTCOMES = ['success', 'needs_review', 'blocked', 'failed', 'cancelled'] as const
export type WorkIntentOutcome = (typeof WORK_INTENT_OUTCOMES)[number]

// Card statuses a finish/abandon may explicitly request. `completed` never
// implies `done` — the agent states the landing lane.
export const WORK_INTENT_CARD_STATUSES = ['review', 'done', 'blocked', 'in_progress'] as const

// Payload bounds — a WorkIntent is a coordination signal, not a transcript.
// Prompts/reasoning are never stored; these caps keep every read bounded.
export const WORK_INTENT_LIMITS = {
  objective: 300,
  plan: 5,
  declared_files: 30,
  observed_files: 100,
  topic_refs: 10,
  area_refs: 10,
} as const

// Presence TTL: an intent whose heartbeat is older than this stops participating
// in preflight (lazy-expired on the next read/start).
export const WORK_INTENT_TTL_MS = 15 * 60_000
